import { createSignal, createEffect, untrack, Show } from 'solid-js';
import { createStore } from 'solid-js/store';
import Text from '../components/Text';
import { useApi, isOk } from '../api';
import { useActiveBot } from '../state/activeBot';
import { useToast } from '../components/MessageBox';
import { useTranslation } from '../hooks/useTranslation';
import type { FarmHelperConfigDto } from '../api';
import styles from './FarmHelperConfig.module.css';
import { firstError } from '../api/envelope';

interface ConfigForm {
    playerName: string;
    minDistance: number;
    maxDistance: number;
    compressAtFreeSlots: number;
}

const emptyForm: ConfigForm = {
    playerName: '',
    minDistance: 2,
    maxDistance: 4,
    compressAtFreeSlots: 3,
};

export default function FarmHelperConfig() {
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();
    const { botId } = useActiveBot();
    const blocksUnit = () => t('FARM_HELPER_UNIT_BLOCKS');
    const slotsUnit = () => t('FARM_HELPER_UNIT_SLOTS');

    const [form, setForm] = createStore<ConfigForm>({ ...emptyForm });
    const [loading, setLoading] = createSignal(true);
    const [saving, setSaving] = createSignal(false);
    const [starting, setStarting] = createSignal(false);
    const [stopping, setStopping] = createSignal(false);

    const applyConfig = (c: FarmHelperConfigDto) => {
        setForm({
            playerName: c.playerName ?? '',
            minDistance: c.minDistance ?? 2,
            maxDistance: c.maxDistance ?? 4,
            compressAtFreeSlots: c.compressAtFreeSlots ?? 3,
        });
    };

    const load = async () => {
        const id = botId();
        if (!id) { setLoading(false); return; }
        const res = await api.farmHelper.getConfig(id);
        if (isOk(res.data)) applyConfig(res.data.value);
        else setForm({ ...emptyForm });
        setLoading(false);
    };

    createEffect(() => {
        const id = botId();
        setLoading(true);
        untrack(() => { void load(); });
        void id;
    });

    const saveConfig = async () => {
        const id = botId();
        if (!id) return;
        if (!form.playerName.trim()) { toast?.error(t('FARM_HELPER_TOAST_PLAYER_REQUIRED')); return; }
        setSaving(true);
        const res = await api.farmHelper.upsertConfig(id, {
            playerName: form.playerName.trim(),
            minDistance: form.minDistance,
            maxDistance: form.maxDistance,
            compressAtFreeSlots: form.compressAtFreeSlots,
        });
        if (isOk(res.data)) { applyConfig(res.data.value); toast?.info(t('FARM_HELPER_TOAST_SAVED')); }
        else toast?.error(firstError(res.data) ?? t('FARM_HELPER_TOAST_SAVE_FAILED'));
        setSaving(false);
    };

    const startTask = async () => {
        const id = botId();
        if (!id) return;
        setStarting(true);
        const res = await api.farmHelper.run(id);
        if (isOk(res.data)) toast?.info(t('FARM_HELPER_TOAST_STARTED'));
        else toast?.error(firstError(res.data) ?? t('FARM_HELPER_TOAST_START_FAILED'));
        setStarting(false);
    };

    const stopTask = async () => {
        const id = botId();
        if (!id) return;
        setStopping(true);
        const res = await api.farmHelper.stop(id);
        if (isOk(res.data)) toast?.info(t('FARM_HELPER_TOAST_STOPPED'));
        else toast?.error(firstError(res.data) ?? t('FARM_HELPER_TOAST_STOP_FAILED'));
        setStopping(false);
    };

    const numField = (label: string, key: keyof ConfigForm, opts: { unit?: string; placeholder?: string } = {}) => (
        <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
            <span class={styles.fieldLabel}>
                <Text val={label} />
                <Show when={opts.unit}><span class={styles.helper} style={{ 'margin-left': '6px' }}>{opts.unit}</span></Show>
            </span>
            <input type="number" step="1" min="0" placeholder={opts.placeholder}
                value={form[key] as number}
                onInput={(e) => setForm(key as any, Number(e.currentTarget.value))} />
        </label>
    );

    return (
        <div class={styles.container}>
            <div class={styles.headerRow}>
                <div class="v" style={{ gap: '4px' }}>
                    <h1><Text val="FARM_HELPER_SETTINGS_TITLE" /></h1>
                    <span class={styles.heroSubtitle}><Text val="FARM_HELPER_SETTINGS_SUBTITLE" /></span>
                </div>
                <Show when={botId()}>
                    <div class="h hcenter" style={{ gap: 'var(--spacing-sm)' }}>
                        <button type="button" class="btn btn-primary" disabled={starting() || !form.playerName.trim()} onClick={() => void startTask()}>
                            <Show when={starting()} fallback={<><i class="fa-solid fa-play" /> <Text val="FARM_HELPER_START" /></>}>
                                <i class="fa-solid fa-spinner fa-spin" />
                            </Show>
                        </button>
                        <button type="button" class="btn btn-secondary" disabled={stopping()} onClick={() => void stopTask()}>
                            <Show when={stopping()} fallback={<><i class="fa-solid fa-stop" /> <Text val="FARM_HELPER_STOP" /></>}>
                                <i class="fa-solid fa-spinner fa-spin" />
                            </Show>
                        </button>
                    </div>
                </Show>
            </div>

            <Show when={botId()} fallback={
                <div class={styles.empty}>
                    <i class="fa-solid fa-robot" style={{ 'font-size': '3rem', opacity: 0.5 }} />
                    <p><Text val="FARM_HELPER_NO_BOT" /></p>
                </div>
            }>
                <Show when={!loading()} fallback={<div class={styles.skeletonCard} />}>
                    <form class="fcard v" style={{ gap: 'var(--spacing-md)' }} onSubmit={(e) => { e.preventDefault(); void saveConfig(); }}>
                        <div class={styles.sectionHeader}>
                            <h2><Text val="FARM_HELPER_FOLLOW" /></h2>
                            <span class={styles.helper}><Text val="FARM_HELPER_FOLLOW_HELP" /></span>
                        </div>

                        <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                            <span class={styles.fieldLabel}>
                                <Text val="FARM_HELPER_PLAYER" />
                                <span class={styles.fieldRequired}>*</span>
                            </span>
                            <input type="text" placeholder="Aktenkoffer_" maxlength="32"
                                value={form.playerName} onInput={(e) => setForm('playerName', e.currentTarget.value)} />
                            <span class={styles.helper}><Text val="FARM_HELPER_PLAYER_HELP" /></span>
                        </label>

                        <div class={styles.fieldRow}>
                            {numField('FARM_HELPER_MIN_DISTANCE', 'minDistance', { unit: blocksUnit(), placeholder: '2' })}
                            {numField('FARM_HELPER_MAX_DISTANCE', 'maxDistance', { unit: blocksUnit(), placeholder: '4' })}
                        </div>
                        <span class={styles.helper}><Text val="FARM_HELPER_DISTANCE_HELP" /></span>

                        <div class={styles.sectionHeader} style={{ 'margin-top': 'var(--spacing-md)' }}>
                            <h2><Text val="FARM_HELPER_COMPRESSION" /></h2>
                            <span class={styles.helper}><Text val="FARM_HELPER_COMPRESSION_HELP" /></span>
                        </div>

                        <div class={styles.fieldRow}>
                            {numField('FARM_HELPER_COMPRESS_AT', 'compressAtFreeSlots', { unit: slotsUnit(), placeholder: '3' })}
                        </div>

                        <div class="h" style={{ 'justify-content': 'flex-end', gap: 'var(--spacing-sm)', 'margin-top': 'var(--spacing-md)' }}>
                            <button type="submit" class="btn btn-primary" disabled={saving()}>
                                <Show when={saving()} fallback={<><i class="fa-solid fa-floppy-disk" /> <Text val="SAVE" /></>}>
                                    <i class="fa-solid fa-spinner fa-spin" />
                                </Show>
                            </button>
                        </div>
                    </form>
                </Show>
            </Show>
        </div>
    );
}
